import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, FileQuestion, Loader2, Plus } from "lucide-react";
import { Container } from "@/components/layout/Container";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/content/EmptyState";
import { CreatePracticeTestForm } from "@/components/study/CreatePracticeTestForm";
import { CramPlanBuilder } from "@/components/study/CramPlanBuilder";
import { useMyAssignments } from "@/hooks/useTests";
import { formatDate } from "@/lib/format";
import type { AssignmentStatus, TestAssignment } from "@/lib/api-types";

const STATUS_TONE: Record<AssignmentStatus, NonNullable<BadgeProps["tone"]>> = {
  ASSIGNED: "neutral",
  STARTED: "accent",
  COMPLETED: "success",
  EXPIRED: "danger",
};

function AssignmentCard({ assignment }: { assignment: TestAssignment }) {
  const isDone = assignment.status === "COMPLETED";
  const isGenerating = assignment.test_status === "GENERATING";
  return (
    <Card className="p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">{assignment.subject_name ?? "Test"}</p>
        <p className="text-xs text-text-muted truncate">
          {isGenerating
            ? "Generating..."
            : `${assignment.num_questions ?? "?"} questions - ${assignment.time_limit_minutes} min${assignment.due_at ? ` - due ${formatDate(assignment.due_at)}` : ""}`}
        </p>
        {isDone && assignment.result ? (
          <p className="text-xs text-success mt-0.5">Scored {assignment.result.percent}%</p>
        ) : null}
      </div>
      {isDone ? (
        <Badge tone={STATUS_TONE[assignment.status]}>Completed</Badge>
      ) : isGenerating ? (
        <Loader2 className="h-4 w-4 animate-spin text-text-muted flex-shrink-0" />
      ) : (
        <Button asChild size="sm">
          <Link to={`/tests/${assignment.assignment_id}/take`}>
            {assignment.status === "STARTED" ? "Continue" : "Start"}
          </Link>
        </Button>
      )}
    </Card>
  );
}

/**
 * Student's assignment inbox -- "A test from Mom, due Sunday", plus (Phase 7)
 * self-serve practice test creation. Scheduled-future parent-assigned
 * assignments never appear here (filtered server-side).
 */
export function TestsInboxPage() {
  const assignments = useMyAssignments();
  const [panel, setPanel] = useState<"none" | "create" | "cram">("none");

  return (
    <Container size="md" className="py-6 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h1 className="font-display text-2xl font-semibold">Tests</h1>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={panel === "cram" ? "ghost" : "secondary"}
            onClick={() => setPanel((p) => (p === "cram" ? "none" : "cram"))}
          >
            <CalendarClock className="h-4 w-4" />
            {panel === "cram" ? "Cancel" : "Cram plan"}
          </Button>
          <Button
            size="sm"
            variant={panel === "create" ? "ghost" : "secondary"}
            onClick={() => setPanel((p) => (p === "create" ? "none" : "create"))}
          >
            <Plus className="h-4 w-4" />
            {panel === "create" ? "Cancel" : "Practice test"}
          </Button>
        </div>
      </div>
      <p className="text-sm text-text-muted mb-6">Tests your parent has assigned you, plus your own practice tests.</p>

      {panel === "create" ? (
        <div className="mb-6">
          <CreatePracticeTestForm onCreated={() => setPanel("none")} />
        </div>
      ) : null}
      {panel === "cram" ? (
        <div className="mb-6">
          <CramPlanBuilder />
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        {assignments.isLoading ? (
          Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)
        ) : (assignments.data ?? []).length === 0 ? (
          <EmptyState
            icon={FileQuestion}
            title="No tests yet"
            body="When a parent assigns you a test, it'll show up here. You can also create your own practice test above."
          />
        ) : (
          (assignments.data ?? []).map((a) => <AssignmentCard key={a.assignment_id} assignment={a} />)
        )}
      </div>
    </Container>
  );
}
